import { FC, useEffect, useState } from 'react';
import { Descriptions, Radio, Checkbox, DatePicker } from 'antd';
import { ICounsel, ICounselMenu } from '../../../models/studentsCounsel.model';
import useCommonCode from '../../../hooks/useCommonCode';
import moment from 'moment';
import CustomTimePicker from '../../CustomTimePicker';
import styled from 'styled-components';
import Button from '../../Button';
import useNotification from '../../../hooks/useNotification';
import useAxios from '../../../hooks/useAxios';
import {
  createStudentCounsel,
  updateStudentCounsel,
} from '../../../api/studentsCounsel.api';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import { useHistory, useLocation } from 'react-router-dom';
import { IStudentClass } from '../../../models/campusClasses.model';
// import useAuth from '../../../hooks/useAuth';
import { ClassContents } from '../../../pages/tutor/class';
import ConfirmModal from '../../modals/ConfirmModal';
import { useTranslation } from 'react-i18next';
import SimpleHeader from '../../layout/SimpleHeader';

const Block = styled.div`
  .ant-descriptions-item {
    padding: 0;
  }
`;

interface IProps {
  formMode: string;
  setFormMode: (mode: string) => void;
  counsel: ICounsel | undefined;
  setCounsel?: (counsel: ICounsel | undefined) => void;
  student?: any;
  menus: ICounselMenu[];
  setIsEdit?: (isEdit: boolean) => void;
  doGetStudentCounsels?: () => void;
  studentInfo?: IStudentClass;
  setContent?: (content: ClassContents) => void;
}

const CounselForm: FC<IProps> = ({
  formMode,
  setFormMode,
  counsel,
  setCounsel,
  student,
  studentInfo,
  menus,
  setIsEdit,
  doGetStudentCounsels,
  setContent,
}) => {
  // const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification, successNotification } = useNotification();
  const { getDetailCode } = useCommonCode();
  const counselTargetGBNDetailCode = getDetailCode('MB0004');
  const counselChannelCodeDetailCode = getDetailCode('MB0007');
  const counselGBNDetailCode = getDetailCode('MB0008');
  const {
    auth: { korName },
  } = useSelector((root: RootState) => root.auth);

  const alwaysMenu = menus.filter(
    ({ enterCounselYn, retierCounselYn }) =>
      enterCounselYn === 'N' && retierCounselYn === 'N',
  );
  const enterCounselMenu = menus.filter(
    ({ enterCounselYn }) => enterCounselYn === 'Y',
  );
  const retierCounselMenu = menus.filter(
    ({ retierCounselYn }) => retierCounselYn === 'Y',
  );
  const { data: res, isLoading, error, execute } = useAxios();

  const [counselTitle, setCounselTitle] = useState('');
  const [counselDate, setCounselDate] = useState('');
  const [counselTime, setCounselTime] = useState('');
  const [counselTargetGBN, setCounselTargetGBN] = useState('P');
  const [counselChannelCode, setCounselChannelCode] = useState('PH');
  const [counselGBN, setCounselGBN] = useState('');
  const [counselTypeDetailCodes, setCounselTypeDetailCodes] = useState<
    number[][]
  >([[], [], []]);
  const [counselContent, setCounselContent] = useState('');
  const [confirmCreateVisible, setConfirmCreateVisible] = useState(false);

  useEffect(() => {
    if (res && !isLoading) {
      if (doGetStudentCounsels) doGetStudentCounsels();
      successNotification(t('student.student.message1'));
      onCancel();
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (counsel) {
      setCounselTitle(counsel.counselTitle);
      setCounselDate(counsel.counselDate);
      setCounselTime(counsel.counselTime);
      setCounselTargetGBN(counsel.counselTargetGBN);
      setCounselChannelCode(counsel.counselChannelCode);
      setCounselGBN(counsel.counselGBN);
      setCounselContent(counsel.counselContent);

      const detailCodes = counsel.counselTypeDetailCodes;
      const newDetailCodes: number[][] = [[], [], []];
      alwaysMenu.forEach(({ counselTypeDetails }) => {
        counselTypeDetails.forEach(({ counselTypeDetailCode }) => {
          if (detailCodes.includes(counselTypeDetailCode)) {
            newDetailCodes[0].push(counselTypeDetailCode);
          }
        });
      });
      enterCounselMenu.forEach(({ counselTypeDetails }) => {
        counselTypeDetails.forEach(({ counselTypeDetailCode }) => {
          if (detailCodes.includes(counselTypeDetailCode)) {
            newDetailCodes[1].push(counselTypeDetailCode);
          }
        });
      });
      retierCounselMenu.forEach(({ counselTypeDetails }) => {
        counselTypeDetails.forEach(({ counselTypeDetailCode }) => {
          if (detailCodes.includes(counselTypeDetailCode)) {
            newDetailCodes[2].push(counselTypeDetailCode);
          }
        });
      });
      setCounselTypeDetailCodes(newDetailCodes);
    }
  }, [counsel]);

  const onSetCounselTypeDetailCodes = (index: number, value: number) => {
    const newValues: number[] = [...counselTypeDetailCodes[index]];
    const valueIndex = newValues.indexOf(value);

    if (valueIndex > -1) {
      newValues.splice(valueIndex, 1);
    } else {
      newValues.push(value);
    }

    const newDetailCodes = [...counselTypeDetailCodes];
    newDetailCodes[index] = newValues;
    setCounselTypeDetailCodes(newDetailCodes);
  };

  const isRetierCounselMenuVisible = (): boolean => {
    const visibleList: number[] = [];
    menus.forEach((menu) =>
      menu.counselTypeDetails.forEach((item) => {
        if (item.additionalItemYn === 'Y')
          visibleList.push(item.counselTypeDetailCode);
      }),
    );

    for (let i = 0; i < visibleList.length; i++) {
      if (
        counselTypeDetailCodes[0].includes(visibleList[i]) ||
        counselTypeDetailCodes[1].includes(visibleList[i]) ||
        counselTypeDetailCodes[2].includes(visibleList[i])
      ) {
        return true;
      }
    }

    return false;
  };

  const onSubmit = () => {
    if (!counselTitle) {
      return infoNotification(t('student.student.message15'));
    }
    if (!counselDate) {
      return infoNotification(t('student.student.message16'));
    }
    if (!counselTime) {
      return infoNotification(t('student.student.message17'));
    }
    if (!counselTargetGBN) {
      return infoNotification(t('student.student.message18'));
    }
    if (!counselChannelCode) {
      return infoNotification(t('student.student.message19'));
    }
    if (!counselGBN) {
      return infoNotification(t('student.student.message20'));
    }
    let numCodes = counselTypeDetailCodes[0].length;
    if (counselGBN !== 'PT') {
      numCodes += counselTypeDetailCodes[1].length;
    }
    if (isRetierCounselMenuVisible()) {
      numCodes += counselTypeDetailCodes[2].length;
    }
    if (numCodes === 0) {
      return infoNotification(t('student.student.message21'));
    }
    if (numCodes > 5) {
      return infoNotification(t('student.student.message22'));
    }
    if (!counselContent) {
      return infoNotification(t('student.student.message23'));
    }

    const req = {
      counselTitle,
      counselDate,
      counselTime,
      counselTargetGBN,
      counselChannelCode,
      counselGBN,
      counselTypeDetailCodes: counselTypeDetailCodes[0],
      counselContent,
    };

    if (counselGBN !== 'PT') {
      req.counselTypeDetailCodes = [
        ...req.counselTypeDetailCodes,
        ...counselTypeDetailCodes[1],
      ];
    }
    if (isRetierCounselMenuVisible()) {
      req.counselTypeDetailCodes = [
        ...req.counselTypeDetailCodes,
        ...counselTypeDetailCodes[2],
      ];
    }

    if (formMode === 'create') {
      if (!student && !counsel) {
        return infoNotification(t('student.student.message24'));
      }

      if (student?.studentCode && student?.campusCode) {
        execute(createStudentCounsel(student.studentCode, student.campusCode, req));
      } else if (counsel?.studentMemberCode && counsel?.campusCode) {
        execute(createStudentCounsel(counsel.studentMemberCode, counsel.campusCode, req));
      }
    } else if (formMode === 'update') {
      if (!counsel) return;
      execute(updateStudentCounsel(counsel.counselCode, req));
    }
  };

  const initInputs = () => {
    setCounselTitle('');
    setCounselDate('');
    setCounselTime('');
    setCounselTargetGBN('');
    setCounselChannelCode('');
    setCounselGBN('');
    setCounselTypeDetailCodes([[], [], []]);
    setCounselContent('');
  };

  const onCancel = () => {
    if (!doGetStudentCounsels && !setContent) {
      return history.push(`/student/consult${location.search}`);
    }

    initInputs();
    if (setCounsel) setCounsel(undefined);
    if (setIsEdit) setIsEdit(false);
    if (setContent) setContent(ClassContents.Counseling);
  };

  const onSetCounselGBN = (code: string) => {
    if (formMode === 'create') {
      setCounselGBN(code);
    } else if (formMode === 'update') {
      setConfirmCreateVisible(true);
    }
  };

  return (
    <Block>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <SimpleHeader
        currentMenu={t('campus.staff.title4')}
      />

      {studentInfo ? (
        <div className="comm-table__area rowType">
          <Descriptions
            bordered
            size="small"
            column={4}
            labelStyle={{ width: '12.5%' }}
            contentStyle={{ width: '12.5%' }}
          >
            <Descriptions.Item label={t('student.student.column5') + '(ID)'}>
              {studentInfo?.memberName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('student.student.column1')}>
              {studentInfo?.memberEngName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.menu.class')}>
              {studentInfo?.className || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('student.student.column64')}>
              {counsel?.studentMemberCode || '-'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : null}

      <div className="comm-table__area rowType">
        <Descriptions bordered column={2} labelStyle={{ width: '100px', textAlign: 'center' }}>
          <Descriptions.Item label={t('common.column.title')} span={1}>
            <input
              className="form-control input-text"
              value={counselTitle}
              onChange={(e) => setCounselTitle(e.target.value)}
              disabled={formMode === 'read'}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.date')} span={1}>
            <div className='comm-gap'>
              <div className="date-picker__area">
                <DatePicker
                  value={counselDate ? moment(counselDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setCounselDate('');
                    else setCounselDate(moment(e).format('YYYYMMDD'));
                  }}
                  disabled={formMode === 'read'}
                />
              </div>
              <div className="time-picker__area">
                <CustomTimePicker
                  className="ml-1"
                  value={counselTime}
                  onSelect={(value) =>
                    setCounselTime(moment(value).format('HH:mm'))
                  }
                  minuteStep={10}
                  disabled={formMode === 'read'}
                />
              </div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column32')} span={1}>
            <div className='auth-radio__area'>
              <Radio.Group
                value={counselTargetGBN}
                onChange={(e) => setCounselTargetGBN(e.target.value)}
                disabled={formMode === 'read'}
              >
                {counselTargetGBNDetailCode.map(
                  ({ detailCode, korName, engName }, index) => (
                    <Radio key={`counselTargetGBN-${index}`} value={detailCode}>
                      {i18n.language === 'ko' ? korName : engName}
                    </Radio>
                  ),
                )}
              </Radio.Group>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column61')} span={1}>
            <div className='auth-radio__area'>
              <Radio.Group
                value={counselChannelCode}
                onChange={(e) => setCounselChannelCode(e.target.value)}
                disabled={formMode === 'read'}
              >
                {counselChannelCodeDetailCode.map(
                  ({ detailCode, korName, engName }, index) => (
                    <Radio key={`counselChannelCode-${index}`} value={detailCode}>
                      {i18n.language === 'ko' ? korName : engName}
                    </Radio>
                  ),
                )}
              </Radio.Group>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column62')} span={1}>
            <div className='auth-radio__area'>
              <Radio.Group
                value={counselGBN}
                onChange={(e) => setCounselGBN(e.target.value)}
                disabled={formMode === 'read'}
              >
                {counselGBNDetailCode.map(({ detailCode, korName, engName }, index) => (
                  <Radio key={`counselGBN-${index}`} value={detailCode}>
                    {i18n.language === 'ko' ? korName : engName}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column35')} span={1}>
            {formMode === 'create' ? korName : counsel?.writerMemberName}
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column36')} span={2}>
            <div className="item-inner__cell">
              {alwaysMenu.map(({ typeName, typeEngName, counselTypeDetails }, index) => (
                <dl key={`always-${index}`}>
                  <dt>{i18n.language === 'ko' ? typeName : typeEngName}</dt>
                  <dd>
                    <div className="input-bx__area">
                      <Checkbox.Group
                        value={counselTypeDetailCodes[0]}
                        disabled={formMode === 'read'}
                      >
                        {counselTypeDetails.map(
                          (
                            {
                              additionalItemYn,
                              detailTypeName,
                              detailTypeEngName,
                              counselTypeDetailCode,
                            },
                            index2,
                          ) => (
                            <Checkbox
                              key={`always-detailCode-${index2}`}
                              value={counselTypeDetailCode}
                              onChange={(e) =>
                                onSetCounselTypeDetailCodes(0, e.target.value)
                              }
                            >
                              {i18n.language === 'ko' ? detailTypeName : detailTypeEngName}
                            </Checkbox>
                          ),
                        )}
                      </Checkbox.Group>
                    </div>
                  </dd>
                </dl>
              ))}
              {counselGBN === '' || counselGBN === 'PT'
                ? null
                : enterCounselMenu.map(
                  ({ typeName, typeEngName, counselTypeDetails }, index) => (
                    <dl
                      key={`enter-${index}`}>
                      <dt>{i18n.language === 'ko' ? typeName : typeEngName}</dt>
                      <dd>
                        <div className="input-bx__area">
                          <Checkbox.Group
                            value={counselTypeDetailCodes[1]}
                            disabled={formMode === 'read'}
                          >
                            {counselTypeDetails.map(
                              (
                                {
                                  additionalItemYn,
                                  detailTypeName,
                                  detailTypeEngName,
                                  counselTypeDetailCode,
                                },
                                index2,
                              ) => (
                                <Checkbox
                                  key={`enter-detailCode-${index2}`}
                                  value={counselTypeDetailCode}
                                  onChange={(e) =>
                                    onSetCounselTypeDetailCodes(1, e.target.value)
                                  }
                                >
                                  {i18n.language === 'ko' ? detailTypeName : detailTypeEngName}
                                </Checkbox>
                              ),
                            )}
                          </Checkbox.Group>
                        </div>
                      </dd>
                    </dl>
                ),
                )}
            </div>
          </Descriptions.Item>
          {isRetierCounselMenuVisible()
            ? retierCounselMenu.map(({ counselTypeDetails }, index) => (
              <Descriptions.Item
                className="align-item-center"
                label={t('student.student.label1')}
                key={`retier-${index}`}
                span={2}
              >
                <div className="input-bx__area">
                  <Checkbox.Group
                    value={counselTypeDetailCodes[2]}
                    disabled={formMode === 'read'}
                  >
                    {counselTypeDetails.map(
                      ({ detailTypeName, detailTypeEngName, counselTypeDetailCode }, index2) => (
                        <Checkbox
                          key={`retier-detailCode-${index2}`}
                          value={counselTypeDetailCode}
                          onChange={(e) =>
                            onSetCounselTypeDetailCodes(2, e.target.value)
                          }
                        >
                          {i18n.language === 'ko' ? detailTypeName : detailTypeEngName}
                        </Checkbox>
                      ),
                    )}
                  </Checkbox.Group>
                </div>
              </Descriptions.Item>
            ))
            : null}
          <Descriptions.Item label={t('student.student.column63')} span={2}>
            <textarea
              rows={10}
              value={counselContent}
              onChange={(e) => setCounselContent(e.target.value)}
              disabled={formMode === 'read'}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={onCancel}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          {formMode === 'read' ? (
            <Button
              className="btn-basic pink-line"
              onClick={() => setFormMode('update')}
              text={t('common.button.edit')}
              // writeAuthYn={writeAuthYn}
            />
          ) : (
            <Button
              className="btn-basic pink"
              onClick={onSubmit}
              text={t('common.button.save')}
              // writeAuthYn={writeAuthYn}
            />
          )}
        </div>
      </div>

      {confirmCreateVisible &&
        <ConfirmModal
          visible={confirmCreateVisible}
          setVisible={setConfirmCreateVisible}
          onConfirm={() => {
            initInputs();
            setFormMode('create');
            setConfirmCreateVisible(false);
          }}
          titleText={t('student.student.title7')}
          messageText={t('student.student.text12')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      }
    </Block>
  );
};

export default CounselForm;
